/**
 * Almacenamiento puro de los tokens internos de Identity.
 *
 * Estrategia de storage:
 *   - access_token  → sessionStorage (efímero; se borra al cerrar la pestaña)
 *   - refresh_token → localStorage  (persistente; permite rehidratar sesión)
 *
 * Este módulo NO hace llamadas de red ni decide flujo: solo lee/escribe/decodifica.
 */

const ACCESS_TOKEN_KEY = "identity.access_token";
const REFRESH_TOKEN_KEY = "identity.refresh_token";

// Margen de seguridad (segundos) para considerar el token expirado antes de tiempo.
const EXPIRY_SKEW_SECONDS = 30;

// ─── Access token (sessionStorage) ─────────────────────────────────────────
export const getAccessToken = () => sessionStorage.getItem(ACCESS_TOKEN_KEY);

export const setAccessToken = (token) => {
  if (token) sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
};

// ─── Refresh token (localStorage) ──────────────────────────────────────────
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const setRefreshToken = (token) => {
  if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token);
};

/** Persiste el par de tokens devuelto por el exchange/refresh. */
export const setTokens = ({access_token, refresh_token} = {}) => {
  setAccessToken(access_token);
  setRefreshToken(refresh_token);
};

/** Borra ambos tokens (logout / refresh fallido). */
export const clearTokens = () => {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// ─── Decodificación / validación de JWT ────────────────────────────────────

/** Decodifica el payload de un JWT. Devuelve null si no es válido. */
export const decodeToken = (jwt) => {
  if (!jwt || typeof jwt !== "string") return null;
  const parts = jwt.split(".");
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    // atob + mapeo manual: evita que caracteres no-ASCII (tildes, ñ, etc.) se corrompan.
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

/** Payload del access_token de Identity actualmente almacenado. */
const getAccessTokenPayload = () => decodeToken(getAccessToken());

/**
 * Indica si el access_token almacenado está expirado (o ausente).
 * @returns {boolean} true si no hay token o ya expiró.
 */
export const isAccessTokenExpired = () => {
  const payload = getAccessTokenPayload();
  if (!payload || typeof payload.exp !== "number") return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSeconds + EXPIRY_SKEW_SECONDS;
};

/**
 * Timestamp (ms) de expiración del access_token almacenado.
 * @returns {number|null} exp en milisegundos, o null si no hay token válido.
 */
export const getAccessTokenExpiry = () => {
  const payload = getAccessTokenPayload();
  if (!payload || typeof payload.exp !== "number") return null;
  return payload.exp * 1000;
};

/** Roles del usuario, leídos del access_token de Identity. */
export const getRoles = () => {
  const payload = getAccessTokenPayload();
  const roles = payload?.roles;
  return Array.isArray(roles) ? roles : null;
};

/** Scopes del usuario, leídos del access_token de Identity. */
export const getScopes = () => {
  const payload = getAccessTokenPayload();
  const scope = payload?.scope;
  if (Array.isArray(scope)) return scope;
  // Algunos emisores devuelven scope como string separado por espacios.
  if (typeof scope === "string") return scope.split(" ").filter(Boolean);
  return null;
};

/** Datos de usuario (nombre/email), leídos del access_token de Identity. */
export const getUser = () => {
  const payload = getAccessTokenPayload();
  if (!payload || (!payload.name && !payload.email)) return null;
  return {name: payload.name ?? null, email: payload.email ?? null};
};
